import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWeightHanging } from '@fortawesome/pro-solid-svg-icons';
import { getReadableTextColor } from 'js/utils/color';
import QuestionNodes from './QuestionNodes';
import {
  LABEL_INSET,
  QUESTION_SET_RADIUS,
  SET_HEADER_HEIGHT_TITLE_ONLY,
  SET_HEADER_HEIGHT_WITH_METRICS,
  TEXT_DARK_HEX,
  TEXT_FOREGROUND_ALPHA,
  TILE_INSET,
  buildQuestionRoot,
  formatScore,
  formatWeight,
  scoreFill,
  scoreIcon,
} from './rubricTreemapUtils';
import styles from './RubricTreemap.module.scss';

export default function QuestionSetNode({
  node,
  minLabelWidth,
  minLabelHeight,
  onQuestionHover,
  onQuestionLeave,
  onQuestionSetHover,
  onQuestionSetLeave,
}) {
  const nodeWidth = Math.max(0, node.x1 - node.x0);
  const nodeHeight = Math.max(0, node.y1 - node.y0);
  const score = node.data.score;
  const fillColor = scoreFill(score);
  const textColor = getReadableTextColor(fillColor, {
    dark: TEXT_DARK_HEX,
    foregroundAlpha: TEXT_FOREGROUND_ALPHA,
  });
  const name = node.data.name || node.data.key || 'Question set';
  const weight = node.data.weight;
  const totalRubricWeight = node.data.totalRubricWeight;
  const questionRoot = buildQuestionRoot(node.data.questions);
  const hasQuestionNodes = questionRoot.children?.length > 0;
  const labelMinHeight = hasQuestionNodes
    ? SET_HEADER_HEIGHT_TITLE_ONLY
    : minLabelHeight;
  const canShowLabel =
    nodeWidth >= minLabelWidth && nodeHeight >= labelMinHeight;
  const showMetrics = canShowLabel && !hasQuestionNodes;
  const labelHeight = canShowLabel
    ? hasQuestionNodes
      ? SET_HEADER_HEIGHT_TITLE_ONLY
      : SET_HEADER_HEIGHT_WITH_METRICS
    : 0;
  const questionAreaX = node.x0 + TILE_INSET;
  const questionAreaY = node.y0 + TILE_INSET + labelHeight;
  const questionAreaWidth = Math.max(0, nodeWidth - TILE_INSET * 2);
  const questionAreaHeight = Math.max(
    0,
    nodeHeight - TILE_INSET * 2 - labelHeight
  );

  return (
    <g key={node.data.id || node.data.key || name}>
      <rect
        x={node.x0}
        y={node.y0}
        width={nodeWidth}
        height={nodeHeight}
        fill={fillColor}
        stroke="none"
        rx={QUESTION_SET_RADIUS}
      />
      <QuestionNodes
        questionAreaX={questionAreaX}
        questionAreaY={questionAreaY}
        questionAreaWidth={questionAreaWidth}
        questionAreaHeight={questionAreaHeight}
        questionRoot={questionRoot}
        name={name}
        onQuestionHover={onQuestionHover}
        onQuestionLeave={onQuestionLeave}
      />
      {labelHeight > 0 && (
        <rect
          x={node.x0}
          y={node.y0}
          width={nodeWidth}
          height={labelHeight}
          fill="transparent"
          onMouseMove={(event) =>
            onQuestionSetHover(event, {
              type: 'question_set',
              label: name,
              score,
              weight,
              totalRubricWeight,
            })
          }
          onMouseLeave={onQuestionSetLeave}
        />
      )}
      {canShowLabel && (
        <foreignObject
          x={node.x0 + LABEL_INSET}
          y={node.y0 + LABEL_INSET}
          width={Math.max(0, nodeWidth - LABEL_INSET * 2)}
          height={Math.max(0, labelHeight - LABEL_INSET + TILE_INSET)}
          pointerEvents="none"
        >
          <div
            xmlns="http://www.w3.org/1999/xhtml"
            className={styles['tile-label']}
            style={{ color: textColor }}
          >
            <div className={styles['tile-title']} title={name}>
              {name}
            </div>
            {showMetrics && (
              <>
                <div className={styles['metric-row']}>
                  <FontAwesomeIcon
                    icon={scoreIcon(score)}
                    className={styles['metric-icon']}
                    fixedWidth
                  />
                  <span className={styles['metric-value']}>
                    {formatScore(score)}
                  </span>
                </div>
                <div className={styles['metric-row']}>
                  <FontAwesomeIcon
                    icon={faWeightHanging}
                    className={styles['metric-icon']}
                    fixedWidth
                  />
                  <span className={styles['metric-value']}>
                    {formatWeight(weight)}
                  </span>
                </div>
              </>
            )}
          </div>
        </foreignObject>
      )}
    </g>
  );
}
